import React, { PropsWithChildren, useState } from "react";
import { Button, Confirm } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Internship, InternshipEditFormModel } from "./types";
import AppForm from "../../components/form/Form";
import agent from "../../app/api/agent";
import { getUpdateRequestPayload } from "./utils";
import useToaster from "../../app/hooks/useToaster";
import FormModal from "../../components/FormModal";
import { useAppForm } from "../../components/form/useForm";
import InternshipForm from "./InternshipForm";

interface Props {
  internship: Internship;
  onUpdate?: () => void;
}

const InternshipEditModal: React.FC<PropsWithChildren<Props>> = ({ children: trigger, internship, onUpdate }) => {
  const [isOpen, setOpen] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { t } = useTranslation();
  const { success, apiError } = useToaster();
  const defaultValues: InternshipEditFormModel = {
    ...internship,
    useHqAddress: !internship.city && !internship.address,
    industryIds: internship.industryIds ?? [],
  };
  const formHook = useAppForm({ defaultValues });

  const isDisabled = isSaving;

  const handleSubmit = async (model: InternshipEditFormModel) => {
    setSaving(true);

    try {
      await agent.Internships.update(internship.id, getUpdateRequestPayload(model));
      success("internship.form.successfully_updated");
      onUpdate?.();
      setOpen(false);
    } catch (error) {
      apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setShowConfirmation(false);
    setSaving(true);

    try {
      await agent.Internships.delete(internship.id);
      success("internship.form.successfully_canceled");
      onUpdate?.();
      setOpen(false);
    } catch (error) {
      apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <>
      <Button primary loading={isSaving} disabled={isDisabled} onClick={formHook.handleSubmit(handleSubmit)}>
        {t("internship.form.update_button")}
      </Button>
      <Button negative disabled={isDisabled} onClick={() => setOpen(false)}>
        {t("common.actions.cancel")}
      </Button>
      <Button color="red" disabled={isDisabled} onClick={() => setShowConfirmation(true)}>
        {t("internship.form.delete_button")}
      </Button>
      <Confirm
        closeOnDimmerClick={false}
        closeOnEscape={false}
        size="tiny"
        content={t("internship.form.delete_confirmation.text")}
        open={showConfirmation}
        confirmButton={t("internship.form.delete_confirmation.yes")}
        cancelButton={t("internship.form.delete_confirmation.no")}
        onCancel={() => setShowConfirmation(false)}
        onConfirm={handleDelete}
      />
    </>
  );

  return (
    <FormModal
      title="internship.edit_internship"
      {...{ isDisabled, isOpen, actions, trigger }}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    >
      <p>{t("internship.form.edit_help")}</p>
      <AppForm formHook={formHook} isDisabled={isDisabled} onSubmit={handleSubmit}>
        <InternshipForm isEditMode />
      </AppForm>
    </FormModal>
  );
};

export default InternshipEditModal;
